import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'app/store';
import { GenericAbortSignal } from 'axios';
import { MprcAutoTabRibbonItem } from 'components/MprcRibbonList/MprcRibbonListAutoTab';
import { CardDtoPaginatedList } from "generated/CoreApi_v1";
import TzUtils from 'lib/TzUtils';
import { coreApiV1 } from "providers/CoreApiProvider";

type CardRibbonTab = {
    ribbonId: string;
    tabId: string;
};

type CardRibbonItem = MprcAutoTabRibbonItem & {
    data: CardDtoPaginatedList | null;
    loading: boolean;
};

type CardRibbon = {
    ribbonId: string;
    items: CardRibbonItem[];
};

type CardFetchDataParam = {
    ribbonId: string;
    tabId: string;
    expanded: boolean;
    index: number;
    maxItems: number;
    signal: GenericAbortSignal | undefined;
    forceReload: boolean;
};

// Estado que será utilizado pelo REDUCER
interface CardState {
    status: "loading" | "posting" | "idle";
    error: ApiProblemErrors | null;
    cache: CardRibbon[];
}

// Estado Inicial
const initialState: CardState = {
    status: "idle",
    error: null,
    cache: [
        {
            ribbonId: "student",
            items: [
                { id: 'trainings', name: "Treinamentos Para você", visible: true, expanded: false, api: coreApiV1.lmsStudent.getAvailableTrainings, loading: false, data: null },
                { id: 'attending', name: "Cursando", visible: true, expanded: false, api: coreApiV1.lmsStudent.getAttendingTrainings, loading: false, data: null },
                { id: 'finished', name: "Finalizados", visible: true, expanded: false, api: coreApiV1.lmsStudent.getFinishedTrainings, loading: false, data: null },
            ]
        }
    ]
};

// Cria Reducer's através de SLICE.
const cardSlice = createSlice({
    name: 'cards',
    initialState,
    reducers: {
        addRibbon: (state, action: PayloadAction<CardRibbon>) => {
            state.cache.push(action.payload);
        },
        expandRibbonTab: (state, action: PayloadAction<CardRibbonTab & { expanded: boolean; }>) => {
            const ribbon = state.cache.find(x => x.ribbonId == action.payload.ribbonId);
            if (ribbon) {
                const list = ribbon.items.find(x => x.id == action.payload.tabId);
                if (list) {
                    list.expanded = action.payload.expanded;
                }
            }
        },
        toogleExpandRibbonTab: (state, action: PayloadAction<{ ribbonId: string, index: number; }>) => {
            const ribbon = state.cache.find(x => x.ribbonId == action.payload.ribbonId);
            if (ribbon && action.payload.index < ribbon.items.length) {
                const list = ribbon.items[action.payload.index];
                if (list) list.expanded = !list.expanded;
            }
        }
    },
    extraReducers: (builder) => {
        ///
        builder.addCase(fetchRibbonCardData.pending,
            (state, { meta: { arg }, payload }) => {
                if (payload != null) {
                    const ribbon = state.cache.find(x => x.ribbonId == arg.ribbonId);
                    if (ribbon) {
                        const list = ribbon.items.find(x => x.id == arg.tabId);
                        if (list) list.loading = true;
                    }
                }
            });
        ///
        builder.addCase(fetchRibbonCardData.rejected,
            (state, { meta: { arg }, payload }) => {
                if (payload != null) {
                    const ribbon = state.cache.find(x => x.ribbonId == arg.ribbonId);
                    if (ribbon) {
                        const list = ribbon.items.find(x => x.id == arg.tabId);
                        if (list) list.loading = false;
                    }
                }
            });

        ///
        builder.addCase(fetchRibbonCardData.fulfilled,
            (state, { meta: { arg }, payload }) => {
                if (payload != null) {
                    const ribbon = state.cache.find(x => x.ribbonId == arg.ribbonId);
                    if (ribbon) {
                        const list = ribbon.items.find(x => x.id == arg.tabId);
                        if (list) {
                            list.data = payload;
                            list.loading = false;
                        }
                    }
                }
            });
    }
});

export const { addRibbon, expandRibbonTab, toogleExpandRibbonTab } = cardSlice.actions;
export default cardSlice.reducer;

//#region Selectors

export const getRibbon = (state: RootState) => (ribbonId: string) => {
    return state.cards.cache.find(x => x.ribbonId == ribbonId);
};

export const getRibbonTab = (state: RootState) => (ribbonId: string, tabId: string) => {
    const ribbon = state.cards.cache.find(x => x.ribbonId == ribbonId);
    if (ribbon != null) {
        const list = ribbon.items.find(x => x.id == tabId);
        if (list != null) return list;
    }
    return null;
};

//#endregion


//#region Async Thunk

export const fetchRibbonCardData =
    createAsyncThunk<CardDtoPaginatedList, CardFetchDataParam, { rejectValue: ApiProblemErrors | string; }>
        ("cards/fechCardData", async (p, thunkApi) => {
            try {
                const { ribbonId, tabId, expanded, maxItems, signal, forceReload } = p;
                const state = thunkApi.getState() as CardState;
                const ribbon = state.cache.find(x => x.ribbonId == ribbonId);
                if (ribbon != null) {
                    const list = ribbon.items.find(x => x.id == tabId);
                    if (list != null) {
                        if (forceReload || list.data == null) {
                            const response = await list.api({
                                searchText: null,
                                pageNumber: 1,
                                pageSize: expanded ? 9999 : maxItems + 1,
                                browserTimeZoneId: TzUtils.browserTimezone()

                            }, { signal });

                            return response.data;
                        }

                        return list.data;
                    }
                    //else thunkApi.rejectWithValue('Invalid Tab Id');
                }
                //else thunkApi.rejectWithValue('Invalid Ribbon Id');
                return { items: [], pageNumber: 1, pageSize: 1, totalPages: 0, totalRows: 0 };
            }
            catch (e) {
                return thunkApi.rejectWithValue(e as ApiProblemErrors);
            }
        });

//#endregion
